import { from } from 'linq-to-typescript';

/**
 * Globaler Sortier-Patch für linq-to-typescript.
 *
 * Hintergrund:
 * linq-to-typescript verwendet bei orderBy/orderByDescending/thenBy/thenByDescending
 * ohne expliziten Comparer intern `Array.prototype.sort(undefined)`. Das sortiert die
 * Sortierschlüssel LEXIKOGRAFISCH (als String) statt numerisch. Bei numerischen
 * Schlüsseln mit unterschiedlicher Stellenzahl (z. B. Unix-Timestamps aus den 1990ern
 * mit 9 Stellen vs. aktuelle mit 10 Stellen) führt das zu falschen Reihenfolgen.
 *
 * Dieser Patch ersetzt die vier Ordering-Methoden auf den linq-Prototypen durch
 * Wrapper, die – sofern kein Comparer übergeben wurde – einen typ-bewussten
 * Default-Comparer injizieren:
 *   - Zahlen werden numerisch sortiert (a - b)
 *   - Alle anderen Typen behalten das Standardverhalten der Bibliothek
 *
 * Bestehende Aufrufe mit explizitem Comparer bleiben unverändert.
 *
 * Der Patch ist idempotent (mehrfacher Import ist unschädlich) und wird als
 * Side-Effect beim Import ausgeführt. In der Angular-App wird er in main.ts
 * vor dem Bootstrap importiert.
 */

const PATCHED_FLAG = Symbol.for('spartax.linqSortPatch.applied');

type OrderingMethod = 'orderBy' | 'orderByDescending' | 'thenBy' | 'thenByDescending';
const ORDERING_METHODS: OrderingMethod[] = ['orderBy', 'orderByDescending', 'thenBy', 'thenByDescending'];

/**
 * Typ-bewusster Default-Comparer.
 * Sortiert Zahlen numerisch, alles andere über die native Standardsortierung.
 */
const defaultComparer = (a: unknown, b: unknown): number => {
    if (typeof a === 'number' && typeof b === 'number') {
        // NaN ans Ende schieben, ansonsten numerisch vergleichen
        if (Number.isNaN(a)) {
            return Number.isNaN(b) ? 0 : 1;
        }
        if (Number.isNaN(b)) {
            return -1;
        }
        return a - b;
    }

    // Fallback auf die native lexikografische Sortierung (Strings, Dates via toString etc.)
    if ((a as never) < (b as never)) {
        return -1;
    }
    if ((a as never) > (b as never)) {
        return 1;
    }
    return 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchPrototype = (prototype: any): void => {
    // Wichtig: hasOwnProperty verwenden, da abgeleitete Prototypen (z. B.
    // OrderedEnumerable.prototype erbt von BasicEnumerable.prototype) das Flag
    // sonst über die Prototyp-Kette "erben" und fälschlich als gepatcht gelten.
    if (!prototype || Object.prototype.hasOwnProperty.call(prototype, PATCHED_FLAG)) {
        return;
    }

    for (const methodName of ORDERING_METHODS) {
        // Nur Methoden wrappen, die tatsächlich auf DIESEM Prototyp definiert sind.
        // Geerbte Methoden werden bereits auf ihrem eigenen Prototyp gepatcht; ein
        // erneutes Wrappen auf der abgeleiteten Ebene würde doppelt injizieren.
        if (!Object.prototype.hasOwnProperty.call(prototype, methodName)) {
            continue;
        }

        const original = prototype[methodName];
        if (typeof original !== 'function') {
            continue;
        }

        prototype[methodName] = function (this: unknown, keySelector: unknown, comparer?: unknown) {
            // Nur einspringen, wenn kein Comparer explizit angegeben wurde.
            const effectiveComparer = comparer ?? defaultComparer;
            return original.call(this, keySelector, effectiveComparer);
        };
    }

    Object.defineProperty(prototype, PATCHED_FLAG, {
        value: true,
        enumerable: false,
        configurable: true,
        writable: false,
    });
};

/**
 * Läuft die komplette Prototyp-Kette eines Enumerables hoch und patcht jede Ebene,
 * die eine eigene Ordering-Methode trägt.
 */
const patchPrototypeChain = (instance: unknown): void => {
    let proto = Object.getPrototypeOf(instance);
    while (proto && proto !== Object.prototype) {
        // Auf eigene (nicht geerbte) Ordering-Methoden prüfen, damit jede Ebene der
        // Kette (BasicEnumerable, OrderedEnumerable, ...) einzeln behandelt wird.
        const hasOwnOrdering = ORDERING_METHODS.some(
            (m) => Object.prototype.hasOwnProperty.call(proto, m) && typeof proto[m] === 'function'
        );
        if (hasOwnOrdering) {
            patchPrototype(proto);
        }
        proto = Object.getPrototypeOf(proto);
    }
};

/**
 * Wendet den Patch auf alle relevanten linq-Prototypen an.
 * Die Prototyp-Ketten werden über Beispiel-Enumerables ermittelt, damit der Patch
 * unabhängig von internen Modulpfaden der Bibliothek funktioniert.
 *
 * Wichtig: orderBy/orderByDescending liegen auf dem Basis-Enumerable-Prototyp,
 * thenBy/thenByDescending jedoch auf dem OrderedEnumerable-Prototyp. Beide Ketten
 * müssen gepatcht werden. Damit die Ordering-Kette für das OrderedEnumerable mit dem
 * bereits gepatchten (numerischen) Default-Comparer aufgebaut wird, patchen wir
 * zuerst das Basis-Enumerable und leiten daraus das OrderedEnumerable ab.
 */
export const applyLinqSortPatch = (): void => {
    // 1. Basis-Enumerable (from([...])) -> orderBy / orderByDescending
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const syncEnumerable: any = from<number>([]);
    patchPrototypeChain(syncEnumerable);

    // 2. OrderedEnumerable (aus orderBy) -> thenBy / thenByDescending
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orderedEnumerable: any = syncEnumerable.orderBy((x: number) => x);
    patchPrototypeChain(orderedEnumerable);
};

applyLinqSortPatch();

export { defaultComparer as linqDefaultComparer };
